import React from 'react';
import {
 Button
} from 'shared/components/ui/button';
import {
 DialogFooter
} from 'shared/components/ui/dialog';
import {
 Loader2
} from 'lucide-react';
import {
 cn
} from 'shared/lib/utils';

interface ChannelSetupLayoutProps {
  children: React.ReactNode;
  onCancel: () => void;
  onSubmit: () => void;
  submitDisabled?: boolean;
  loading?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  className?: string;
  footerClassName?: string;
  onBack?: () => void;
  title?: string;
  isDashboard?: boolean;
  // Lock the dialog to a constant height so its size never changes between steps
  // (content scrolls internally instead of growing/shrinking the box).
  fixedHeight?: boolean;
}

export const ChannelSetupLayout: React.FC<ChannelSetupLayoutProps> = ({
  children,
  onCancel,
  onSubmit,
  submitDisabled = false,
  loading = false,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  className = '',
  footerClassName = '',
  onBack,
  title,
  isDashboard = false,
  fixedHeight = false
}) => {
  return (
      <div className={cn(
          'flex flex-col',
          // A fixed-height box holds its size across every step (no animated grow/shrink);
          // otherwise fall back to the original min-height + transition behavior.
          fixedHeight
              ? 'w-full max-w-[900px] bg-background h-[660px] max-h-[88vh]'
              : 'transition-all duration-300 ease-in-out w-full max-w-[900px] min-h-0 bg-background sm:min-h-[660px]',
          className
      )}>
          {/* Dashboard Header */}
          {(onBack || title) && (
              <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
                  <div className="flex items-center gap-4">
                      {onBack && (
                          <Button
                              variant="ghost"
                              size="sm"
                              onClick={onBack}
                              className="h-8 px-2 text-muted-foreground hover:text-primary transition-colors flex items-center gap-2 -ml-2"
                          >
                              <span className="text-lg">←</span>
                              <span className="text-xs font-medium">Back</span>
                          </Button>
                      )}
                      {title && <h2 className="text-sm font-bold text-foreground">{title}</h2>}
                  </div>
              </div>
          )}

          <div className={cn(
              'flex-grow min-h-0',
              // When height is locked, let the inner columns own their scrolling so the
              // box stays put; otherwise scroll the whole content area as before.
              fixedHeight ? 'overflow-hidden' : 'overflow-y-auto',
              !isDashboard && 'px-12 py-8 max-w-2xl mx-auto w-full'
          )}>
              {children}
          </div>

          <DialogFooter className={cn(
              'mt-auto py-4 px-6 border-t border-border bg-muted/30', // Reduced py from 5 to 4
              'flex items-center justify-end gap-2', // Reduced gap from 3 to 2
              footerClassName
          )}>
              <Button
                  onClick={onCancel}
                  variant="outline"
                  disabled={loading}
                  className="px-6 h-10 border-border text-foreground hover:bg-muted transition-colors"
              >
                  {cancelLabel}
              </Button>
              <Button
                  onClick={onSubmit}
                  disabled={submitDisabled || loading}
                  className="px-8 h-10 bg-primary hover:bg-primary-hover text-white shadow-sm transition-all active:scale-95"
              >
                  {loading ? (
                      <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processing...
                      </>
                  ) : (
                      submitLabel
                  )}
              </Button>
          </DialogFooter>
      </div>
  );
};
